import { Fragment } from "react";
import type { Job } from "providers/HostedScrapingProvider";
import { countOfAllTasks, type RunningProjectInfo } from "providers/HostedScrapingProvider/types";
import ActionsMenu, { ActionMenuItem } from "components/ActionsMenu";
import { formatDate } from "components/hosted-scraping/ProjectFormatDate";
import { formatCreditsSpent } from "components/hosted-scraping/creditsSpent";
import { formatTimeHoursMins } from "components/hosted-scraping/project-summary/ProjectSummarySidebarHostedScraper";
import { jobInfoMessageAreaVisible, PlaceHolderJobToMarkQueuedJob, lineClassName, jobErrorMessageToHumanReadable, shouldShowFailureReportFallback, hasResult, resultFormatLabel, jobResultUrl } from "components/hosted-scraping/job-list/utils";
import { JobStatus } from "components/hosted-scraping/job-list/JobStatus";
import { JobResponseStatus } from "components/hosted-scraping/job-list/JobResponseStatus";
import { FinishedAtCell } from "components/hosted-scraping/job-list/FinishedAtCell";
import { DeliveryButton } from "components/hosted-scraping/job-list/DeliveryButton";
import { JobConcurrencyInfo } from "components/hosted-scraping/job-list/JobConcurrencyInfo";
import { JobErrorMessage } from "components/hosted-scraping/job-list/JobErrorMessage";
import { JobQueuedInfo } from "components/hosted-scraping/job-list/JobQueuedInfo";
import { TerminationReasonsJobInfo } from "components/hosted-scraping/job-list/TerminationReasonsJobInfo";
import { JobListSeparator } from "components/hosted-scraping/job-list/JobListSeparator";
import ActiveSubscription from "types/ActiveSubscription";
import { HIDDEN_TERMINATION_SLUGS } from "components/hosted-scraping/APIErrorList";
import { cx } from "utils";
import { PureTooltip } from "components/Tooltip";

/*
 * This turns a job into a line and an optional message below the line
 */
export const jobLine = (
  projectId: number,
  runningProjectInfo: RunningProjectInfo | undefined,
  activeSubscription: ActiveSubscription | null | undefined,
  onProgressDetailsClicked: (jobId: number) => void,
  onCancelJobClicked: (jobId: number) => void,
  onViewIssuesClicked?: (jobId: number, index: number) => void,
  onViewUrlsClicked?: (jobId: number) => void
) => (job: Job | PlaceHolderJobToMarkQueuedJob, index: number) => {
    const startDate = job.startedAt || job.createdAt;
    // The queued placeholder (id <= 0) has no job page or result yet.
    const isRealJob = job.id > 0;
    const jobHasResult = hasResult(job as Job);
    const jobHasSubTasks = countOfAllTasks(job as Job) > 0;

    const actions: ActionMenuItem[] = [
      { label: "View Results", onClick: () => onViewUrlsClicked?.(job.id) },
      {
        label: jobHasResult ? `Download results (${resultFormatLabel(job as Job)})` : "Download results",
        onClick: () => window.open(jobResultUrl(job.publicJobId), "_blank", "noreferrer"),
        disabled: !jobHasResult,
      },
    ];

    const {jobInfoVisible, remainingTotalSeconds, progressPercentage} = jobInfoMessageAreaVisible(job as Job, activeSubscription?.planSlug, activeSubscription?.apiConcurrencyLimit ?? 1);
    const remainingEstimationLabel = formatTimeHoursMins(remainingTotalSeconds ?? 0);

    /*
     * These are the elements in the line
     */
    const jobInfoLine = (
      <Fragment key={`job-${job.id}`}>
        <div className={lineClassName}>
          <JobStatus
            job={job}
            progressPercentage={progressPercentage}
            timeEstimationLabel={jobInfoVisible ? undefined : remainingEstimationLabel}
            cancelJobButtonClicked={() => onCancelJobClicked(job.id)}
          />
        </div>
        <div className={lineClassName}><JobResponseStatus job={job as Job}/></div>
        <div className={lineClassName}>{formatDate(startDate)}</div>
        <div className={lineClassName}><FinishedAtCell job={job as Job} onProgressDetailsClicked={() => onProgressDetailsClicked(job.id)}/></div>
        <div className={lineClassName}>{formatCreditsSpent(job.credits)}</div>
        <div className={lineClassName}><DeliveryButton job={job as Job} projectId={projectId} /></div>
        <div className={cx(lineClassName, 'pl-0')}>
          {isRealJob && (
            <PureTooltip content={!jobHasSubTasks && "No URLs to view for this job."}>
              <ActionsMenu items={actions} label="Select action" disabled={!jobHasSubTasks} />
            </PureTooltip>
          )}
        </div>
      </Fragment>
    );

    /*
     * We add optional messages below the line
     */
    const elements = [jobInfoLine];

    // CTA buttons only on the first job — the user may have fixed the problem since older runs.
    // NOTE: assumes the default last_run_first sort (index 0 == latest run). Under other sort
    // orders index 0 isn't the latest run; revisit when phase-2 per-error CTAs are added.
    const showCTAButtons = index === 0;
    // The running job information
    if (jobInfoVisible) {
      const jobInfo = <JobConcurrencyInfo key={`info_bar-${job.id}`} remainingTotalSeconds={remainingTotalSeconds}/>;
      elements.push(jobInfo);
    } else if (job.status === 'error') {
      const errorDetails = jobErrorMessageToHumanReadable(job.result?.errorMessage);
      if (errorDetails !== null) {
        let { message, fixRelatedMessage, fixUrl } = errorDetails;
        // Only the very few job can get a fix button
        if (index !== 0) {
          fixUrl = undefined;
          fixRelatedMessage = undefined;
        }
        const jobInfo = <JobErrorMessage key={`error_bar-${job.id}`} showCTAButtons={showCTAButtons} errorMessage={message} fixRelatedMessage={fixRelatedMessage} fixUrl={fixUrl} />;
        elements.push(jobInfo);
      }
    } else if (job.status === 'queued') {
      const jobInfo = <JobQueuedInfo key={`queued_info-${job.id}`} projectId={projectId} runningProjectInfo={runningProjectInfo} />;
      elements.push(jobInfo);
    }
    const stats = job.terminationReasonStats ?? {};
    const visibleSlugs = Object.keys(stats).filter(slug => !HIDDEN_TERMINATION_SLUGS.has(slug));
    if (visibleSlugs.length > 0) {
      let issueCount = 0;
      for (const slug of visibleSlugs) {
        issueCount += stats[slug];
      }
      const terminationReasonsInfo = <TerminationReasonsJobInfo key={`termination_reasons_info-${job.id}`} issueCount={issueCount} onViewDetails={() => onViewIssuesClicked?.(job.id, index)}/>;
      elements.push(terminationReasonsInfo);
    } else if (shouldShowFailureReportFallback(job)) {
      // No reason slugs to break down, so the count is the whole failed set and "View details"
      // hands off to the error report.
      const failureReportInfo = <TerminationReasonsJobInfo key={`failure_report_info-${job.id}`} issueCount={job.failedTasks} onViewDetails={() => onViewIssuesClicked?.(job.id, index)}/>;
      elements.push(failureReportInfo);
    }

    // Separator
    elements.push(<JobListSeparator key={`separator-${job.id}`} />);

    return elements;
};
